import pdfMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import handlePurchase from '@/services/modules/purchase'
import figureFormatter from '@/services/utils/figureFormatter'
import { formatToDdMmYy } from '@/services/utils/global'
import pdfHeaderFooter from '@/services/utils/pdf/pdfHeaderFooter'
import { useStore } from 'vuex'

pdfMake.vfs = pdfFonts.pdfMake.vfs

const paymentRequestVoucherPdf = () => {
    const { currencyToDigitWord } = handlePurchase()
    const { commaFormat } = figureFormatter()
    const { getHeader, getFooter } = pdfHeaderFooter()
    const store = useStore()
    const userName = store.state.user.user.name

    const formatDay = (date) => {
        if (! date) return ''
        return formatToDdMmYy(date)
    }

    const isDisbursement = (data) => data.is_disbursement === true

    const getInfoLeftStack = (data) => {
        const stack = [
            { text: [{ text: 'Pay to: ', bold: true }, { text: data.party_name || '' }], margin: [0, 0, 0, 2] },
            { text: [{ text: 'Paid From (Account): ', bold: true }, { text: data.paid_from || '' }], margin: [0, 0, 0, 2] }
        ]
        if (data.cheque_number) stack.push({ text: [{ text: 'Cheque # ', bold: true }, { text: data.cheque_number }], margin: [0, 0, 0, 2] })
        if (data.cheque_date) stack.push({ text: [{ text: 'Cheque Date: ', bold: true }, { text: formatDay(data.cheque_date) }], margin: [0, 0, 0, 2] })
        if (isDisbursement(data) && data.cheque_clearance_date) stack.push({ text: [{ text: 'Cheque Clearance Date: ', bold: true }, { text: formatDay(data.cheque_clearance_date) }] })
        return stack
    }

    const getInfoRightRow = (label, value, isLast) => {
        const margin = isLast ? [0, 0, 0, 0] : [0, 0, 0, 2]
        return {
            margin: margin,
            columns: [
                { width: '*', text: label, bold: true, alignment: 'right' },
                { width: 'auto', text: ' ' + value, alignment: 'left' }
            ]
        }
    }

    const getInfoRightStack = (data) => {
        const dateLabel = isDisbursement(data) ? 'Voucher Date:' : 'Payment Request Date:'
        const dateValue = isDisbursement(data) ? data.date : data.payment_request_date
        return [
            getInfoRightRow('Voucher No:', data.voucher_no || '', false),
            getInfoRightRow(dateLabel, formatDay(dateValue), true)
        ]
    }

    const getInfoBlock = (data) => {
        return {
            margin: [0, 10, 0, 0],
            columns: [
                { width: '50%', stack: getInfoLeftStack(data) },
                { width: '50%', stack: getInfoRightStack(data) }
            ]
        }
    }

    const getBillTableHeader = () => {
        return [
            { text: 'SL #', bold: true },
            { text: 'Bill Number & Date', bold: true },
            { text: 'Description', bold: true },
            { text: 'Bill Amount', bold: true, alignment: 'right' },
            { text: 'TDS', bold: true, alignment: 'right' },
            { text: 'VDS', bold: true, alignment: 'right' },
            { text: 'Already Paid / Adjusted', bold: true, alignment: 'right' },
            { text: 'Net Payment', bold: true, alignment: 'right' }
        ]
    }

    const getBillRow = (row, index, showDiscount) => {
        const cols = [
            { text: index + 1 },
            { stack: [{ text: row.bill_number || '', bold: true }, { text: formatDay(row.payment_date) }] },
            { text: row.description || '' },
            { text: commaFormat(row.bill_amount), alignment: 'right' },
            { text: commaFormat(row.tds_payable_amount), alignment: 'right' },
            { text: commaFormat(row.vds_payable_amount), alignment: 'right' }
        ]
        if (showDiscount) cols.push({ text: commaFormat(row.discount_receipt_amount), alignment: 'right' })
        cols.push({ text: commaFormat(row.already_paid_adjusted), alignment: 'right' })
        cols.push({ text: commaFormat(row.paid_amount), alignment: 'right' })
        return cols
    }

    const sumBy = (rows, key) => rows.reduce(function (carry, row) { return carry + row[key] }, 0)

    const getBillTotalRow = (rows, showDiscount) => {
        const cols = [
            { text: 'TOTAL', colSpan: 3, bold: true }, '', '',
            { text: commaFormat(sumBy(rows, 'bill_amount')), bold: true, alignment: 'right' },
            { text: commaFormat(sumBy(rows, 'tds_payable_amount')), bold: true, alignment: 'right' },
            { text: commaFormat(sumBy(rows, 'vds_payable_amount')), bold: true, alignment: 'right' }
        ]
        if (showDiscount) cols.push({ text: commaFormat(sumBy(rows, 'discount_receipt_amount')), bold: true, alignment: 'right' })
        cols.push({ text: commaFormat(sumBy(rows, 'already_paid_adjusted')), bold: true, alignment: 'right' })
        cols.push({ text: commaFormat(sumBy(rows, 'paid_amount')), bold: true, alignment: 'right' })
        return cols
    }

    const getBillTableHeaderWithDiscount = (showDiscount) => {
        const header = getBillTableHeader()
        if (! showDiscount) return header
        header.splice(6, 0, { text: 'Discount', bold: true, alignment: 'right' })
        return header
    }

    const getBillTableWidths = (showDiscount) => {
        if (showDiscount) return ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto']
        return ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto']
    }

    const getBillTable = (rows) => {
        const showDiscount = sumBy(rows, 'discount_receipt_amount') > 0
        const body = [getBillTableHeaderWithDiscount(showDiscount)]
        rows.forEach(function (row, index) { body.push(getBillRow(row, index, showDiscount)) })
        body.push(getBillTotalRow(rows, showDiscount))
        return {
            margin: [0, 10, 0, 0],
            table: {
                headerRows: 1,
                widths: getBillTableWidths(showDiscount),
                body: body
            },
            layout: {
                fillColor: function (rowIndex) { return rowIndex === 0 ? '#f3f2f7' : null },
                hLineWidth: function () { return 0.5 },
                vLineWidth: function () { return 0.5 }
            }
        }
    }

    const getLedgerTableHeader = () => {
        return [
            { text: 'Accounts Description', bold: true },
            { text: 'Line Description', bold: true },
            { text: 'Party Name', bold: true },
            { text: 'Amount', bold: true, alignment: 'right' }
        ]
    }

    const getLedgerRow = (line) => {
        const amount = line.debit || line.credit
        return [
            { text: line.account_head || '' },
            { text: line.note || '' },
            { text: line.party_name || '' },
            { text: commaFormat(amount), alignment: 'right' }
        ]
    }

    const getLedgerTable = (lines, totalAmount) => {
        const body = [getLedgerTableHeader()]
        lines.forEach(function (line) { body.push(getLedgerRow(line)) })
        body.push([
            { text: 'Total',
                colSpan: 3,
                alignment: 'right',
                bold: true
            }, '', '',
            { text: commaFormat(totalAmount), bold: true, alignment: 'right' }
        ])
        return {
            margin: [0, 10, 0, 0],
            table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto', 'auto'],
                body: body
            },
            layout: {
                fillColor: function (rowIndex) { return rowIndex === 0 ? '#f3f2f7' : null },
                hLineWidth: function () { return 0.5 },
                vLineWidth: function () { return 0.5 }
            }
        }
    }

    const getSignatureLine = (step, width) => {
        const stack = []
        if (step.signature) stack.push({ image: step.signature, width: 70, height: 30, alignment: 'center', margin: [0, 0, 0, 2] })
        if (! step.signature) stack.push({ text: '', margin: [0, 0, 0, 30] })
        stack.push({
            table: { widths: ['*'], body: [[{ text: '', border: [false, false, false, true] }]] },
            layout: { hLineWidth: function () { return 1 }, paddingTop: function () { return 0 }, paddingBottom: function () { return 0 } }
        })
        stack.push({ margin: [0, 5, 0, 0], text: step.label || '', bold: true })
        if (step.name) stack.push({ text: step.name })
        if (step.date) stack.push({ text: formatDay(step.date) })
        return { width: width, stack: stack }
    }

    const perSignatureRow = 4

    const chunkSteps = (steps) => {
        const chunks = []
        let index = 0
        while (index < steps.length) {
            chunks.push(steps.slice(index, index + perSignatureRow))
            index += perSignatureRow
        }
        return chunks
    }

    const buildSignatureColumns = (rowSteps, width) => {
        const columns = rowSteps.map(function (step) { return getSignatureLine(step, width) })
        while (columns.length < perSignatureRow) {
            columns.push({ width: width, text: '' })
        }
        return columns
    }

    const buildSignatureRow = (steps) => {
        if (! steps.length) return { text: '' }
        const width = (100 / perSignatureRow).toFixed(4) + '%'
        return {
            stack: chunkSteps(steps).map(function (rowSteps) {
                return {
                    columnGap: 15,
                    margin: [0, 0, 0, 18],
                    columns: buildSignatureColumns(rowSteps, width)
                }
            })
        }
    }

    const getSignatures = (data) => {
        return buildSignatureRow(getSignatureSteps(data))
    }

    const getSignatureSteps = (data) => {
        if (! isDisbursement(data)) {
            return [
                { label: 'Prepared By' },
                { label: 'Reviewed By' },
                { label: 'Approved By' },
                { label: 'Received By' }
            ]
        }
        return data.approval_steps || []
    }

    const getSignatureRowCount = (data) => {
        return chunkSteps(getSignatureSteps(data)).length
    }

    const hasSignatureImage = (data) => {
        return getSignatureSteps(data).some(function (step) { return step.signature })
    }

    const getSignatureRowHeight = (data) => {
        if (hasSignatureImage(data)) return 87
        return 55
    }

    const getSignatureBottomMargin = (data) => {
        return 90 + getSignatureRowCount(data) * getSignatureRowHeight(data)
    }

    const getSignatureFooter = (data, userName, qrcode) => {
        return {
            stack: [
                {
                    margin: [60, 0, 60, 12],
                    stack: [getSignatures(data)]
                },
                getFooter(userName, qrcode)
            ]
        }
    }

    const getTable = (data) => {
        if (data.txn_type === 'bill_payment') return getBillTable(data.bill_rows)
        return getLedgerTable(data.ledger_lines, data.total_amount)
    }

    const transactionTypeLabels = {
        bill_payment: 'Bill Payment Voucher',
        general_payment: 'General Payment Voucher',
        general_contra: 'Contra Voucher'
    }

    const getTransactionTypeLabel = (data) => {
        const label = transactionTypeLabels[data.txn_type]
        if (! label) return ''
        return `${label}`
    }

    const getVoucherMemo = (data) => {
        if (! data.note) return { text: '' }
        return { margin: [0, 8, 0, 0], text: [{ text: 'Voucher Memo: ', bold: true }, { text: data.note }] }
    }

    const getTitle = (data) => {
        if (isDisbursement(data)) return 'Payment Disbursement'
        return 'Payment Request'
    }

    const getContent = (data) => {
        return [
            {
                text: getTitle(data),
                fontSize: 16,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 6]
            },
            {
                text: getTransactionTypeLabel(data),
                alignment: 'center',
                bold: true,
                fontSize: 9,
                margin: [0, 0, 0, 10]
            },

            getInfoBlock(data),

            getTable(data),
            {
                margin: [0, 16, 0, 0],
                text: [
                    { text: 'Amount in Words: ', bold: true },
                    { text: data.total_amount ? currencyToDigitWord(data.total_amount.toFixed(2), false, true) : '' }
                ]
            },
            getVoucherMemo(data)
        ]
    }

    const exportToPDF = (company, data, qrcode) => {
        const doc = {
            pageSize: 'A4',
            pageMargins: [60, 100, 60, getSignatureBottomMargin(data)],
            header: getHeader(company),
            footer: (currentPage, pageCount) => {
                if (currentPage === pageCount) return getSignatureFooter(data, userName, qrcode)
                return getFooter(userName, qrcode)
            },
            content: getContent(data),
            defaultStyle: {
                color: 'black',
                fontSize: 9,
            },
            info: { title: data.voucher_no }
        }
        pdfMake.createPdf(doc).open()
    }

    return { exportToPDF }
}

export default paymentRequestVoucherPdf
